import { z } from 'zod';

import { ensureFreshPlatformJwt } from '../sso.js';

/**
 * The skill the judge drafted, ready to prefill the editor with. Mirrors
 * `SuggestedSkillDraft` in cti-conversation-service.
 */
const SkillDraftSchema = z.object({
  trigger: z.string(),
  title: z.string(),
  description: z.string(),
  instructions: z.string(),
  tags: z.array(z.string()).default([]),
});

/**
 * Wire row for `GET /v1/skills/suggestions?conversation_id=…`. snake_case on the wire, like every
 * other client route; camelCase starts at the boundary below.
 */
const SkillSuggestionSchema = z.object({
  id: z.string(),
  conversation_id: z.string(),
  message_id: z.string(),
  reason: z.string(),
  draft: SkillDraftSchema.nullable(),
});

/**
 * Wire row for `GET /v1/skills/suggestions/candidates` — the same offer plus where it came from.
 *
 * The provenance fields are what the inline offer does not need and the list cannot do without: in the
 * list an offer can be an hour old and about a conversation the user has since left, and with only a
 * title and a reason on the card it reads as an invention. Both are optional on the schema so a server
 * older than this build still parses, and the card simply says less.
 */
const SkillSuggestionCandidateSchema = SkillSuggestionSchema.extend({
  created_at: z.string().optional(),
  conversation_title: z.string().nullish(),
});

export type SkillDraft = z.infer<typeof SkillDraftSchema>;

/** A standing offer to turn a finished turn into a skill, as the UI consumes it. */
export interface SkillSuggestion {
  id: string;
  conversationId: string;
  messageId: string;
  reason: string;
  draft: SkillDraft | null;
}

/** The same offer in the library's list, where it has to say which work it came out of and when. */
export interface SkillSuggestionCandidate extends SkillSuggestion {
  conversationTitle: string | null;
  /** ISO timestamp of when the platform raised the offer; null on a server that does not send it. */
  createdAt: string | null;
}

const API_BASE = '/v1';

async function headers(): Promise<HeadersInit> {
  const jwt = await ensureFreshPlatformJwt();
  return {
    'Content-Type': 'application/json',
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
}

/**
 * The caller's open suggestions in one conversation — normally zero or one.
 *
 * Fails soft to an empty list. This is an unrequested nicety layered on a conversation the user opened
 * to do something else, so a server that is unreachable, or older than this build, must cost them
 * nothing but the absence of a suggestion.
 */
export async function fetchPendingSuggestions(
  conversationId: string,
): Promise<SkillSuggestion[]> {
  try {
    const query = new URLSearchParams({ conversation_id: conversationId });
    const res = await fetch(`${API_BASE}/skills/suggestions?${query}`, {
      headers: await headers(),
    });
    if (!res.ok) return [];
    const parsed = z
      .array(SkillSuggestionSchema)
      .safeParse((await res.json()) as unknown);
    if (!parsed.success) return [];
    return parsed.data.map((row) => ({
      id: row.id,
      conversationId: row.conversation_id,
      messageId: row.message_id,
      reason: row.reason,
      draft: row.draft,
    }));
  } catch {
    return [];
  }
}

/**
 * Report that the offer is on screen.
 *
 * The server cannot know this, and the difference matters: an offer that expires having never been
 * seen must cost the user nothing, while one they saw and left alone is a mild "not now". Without this
 * call the server treats every expiry as unseen — lenient, and wrong in the user's favour, which is the
 * right way round to be wrong.
 *
 * Fire-and-forget. Nothing on screen depends on the answer.
 */
export async function reportSuggestionSeen(id: string): Promise<void> {
  try {
    await fetch(
      `${API_BASE}/skills/suggestions/${encodeURIComponent(id)}/seen`,
      {
        method: 'POST',
        headers: await headers(),
      },
    );
  } catch {
    /* an unreported sighting simply reads as never seen, which costs the user nothing */
  }
}

/**
 * Report that the user opened the offer — expanded the line to read the draft, or pressed Review in the
 * Suggested list.
 *
 * This, not the sighting above, is what an ignore is counted from. A sighting is an 11px line crossing
 * the viewport, usually 20 seconds after the answer, when the user is often already looking elsewhere;
 * charging silence for three of those muted whole users for a month over offers they never noticed.
 * Opening the offer is the user proving they read it, so an offer opened and then left alone is a real
 * "not now", and one merely scrolled past is nothing.
 *
 * Fire-and-forget, like the sighting: an unreported open reads as never opened, which costs the user
 * nothing but the quiet they might have earned.
 */
export async function reportSuggestionOpened(id: string): Promise<void> {
  try {
    await fetch(
      `${API_BASE}/skills/suggestions/${encodeURIComponent(id)}/opened`,
      {
        method: 'POST',
        headers: await headers(),
      },
    );
  } catch {
    /* an unreported open reads as never opened, which is the lenient answer */
  }
}

/**
 * Everything the caller could still turn into a skill, across every conversation and every workspace.
 *
 * The pull half of the feature. An inline offer that timed out unanswered — or that fresher work pushed
 * out of the slot — is not thrown away; it is listed here, where the user meets it while managing skills
 * rather than mid-task. Fails soft to an empty list for the same reason the per-conversation read does.
 *
 * Unscoped, and it used to take a workspace. The skill the user creates from any of these is their own
 * private one, so the list is theirs too — filtering it by the workspace switcher meant offers vanished
 * when the user moved, which reads as offers being withdrawn. Each row says which conversation it came
 * from instead, which is the honest way to answer "why is this here".
 */
export async function fetchSuggestionCandidates(): Promise<
  SkillSuggestionCandidate[]
> {
  try {
    const res = await fetch(`${API_BASE}/skills/suggestions/candidates`, {
      headers: await headers(),
    });
    if (!res.ok) return [];
    const parsed = z
      .array(SkillSuggestionCandidateSchema)
      .safeParse((await res.json()) as unknown);
    if (!parsed.success) return [];
    return parsed.data.map((row) => ({
      id: row.id,
      conversationId: row.conversation_id,
      messageId: row.message_id,
      reason: row.reason,
      draft: row.draft,
      conversationTitle: row.conversation_title ?? null,
      createdAt: row.created_at ?? null,
    }));
  } catch {
    return [];
  }
}

/**
 * Put the offer aside — the ✕ on the line. Not an answer: the server lapses the offer now instead of at
 * its TTL, and it waits in the Suggested list. Whether that counts as an ignore is decided by whether
 * the user had opened it, exactly as for an offer left to lapse. Resolves either way, like a dismiss.
 */
export async function setSuggestionAside(id: string): Promise<void> {
  try {
    await fetch(
      `${API_BASE}/skills/suggestions/${encodeURIComponent(id)}/later`,
      {
        method: 'POST',
        headers: await headers(),
      },
    );
  } catch {
    /* the line is already gone; a lost request means the offer lapses at its TTL instead */
  }
}

/**
 * Record that the user said no. Resolves either way: the affordance is already gone from their screen
 * by the time this settles, and a failed dismiss costs at most one more offer later.
 */
export async function dismissSuggestion(id: string): Promise<void> {
  try {
    await fetch(
      `${API_BASE}/skills/suggestions/${encodeURIComponent(id)}/dismiss`,
      {
        method: 'POST',
        headers: await headers(),
      },
    );
  } catch {
    /* the user has moved on; nothing here is worth interrupting them for */
  }
}
